import {
  DefaultModality,
  TargetDisplaySize,
  VideoPreference,
  VideoPreferences,
  VideoPriorityBasedPolicy,
} from 'amazon-chime-sdk-js';
import { Layout } from "../video.types";
import type { TileState, VideoSourceState } from "../video.types";

type GridState = { layout: Layout };

type VideoSourceWithType = { attendeeId: string; type: VideoSourceType };

enum VideoSourceType {
  CONTENT_SHARE = 'contentShare',
  ACTIVE_SPEAKER = 'activeSpeaker',
  OTHER = 'other',
}

export const isContentShare = (sourceId: string): boolean =>
  new DefaultModality(sourceId).hasModality(DefaultModality.MODALITY_CONTENT);

/** Derive video-enabled from tile state: 'video' or 'frozen' means the
 *  downlink policy should subscribe to this attendee's stream. Pointedly
 *  excludes 'bandwidth_suppressed' — those tiles render as avatar + pill
 *  and we don't want them re-added to the renderable set by helpers. */
const isTileVideoEnabled = (state: TileState | undefined): boolean =>
  state === 'video' || state === 'frozen';

export const calculateVideoSourcesToBeRendered = (
  gridState: GridState,
  videoSourceState: VideoSourceState,
  attendeeTileState: Record<string, TileState>
): VideoSourceWithType[] => {
  const { layout } = gridState;
  const {
    activeSpeakersWithCameraSource,
    cameraSources,
    contentShareId,
    hasLocalVideo,
  } = videoSourceState;
  const videoSources: VideoSourceWithType[] = [];
  let commonSources: string[];

  // First, add content share
  for (const attendeeId of Object.keys(attendeeTileState)) {
    if (isContentShare(attendeeId) && isTileVideoEnabled(attendeeTileState[attendeeId])) {
      videoSources.push({ attendeeId, type: VideoSourceType.CONTENT_SHARE });
    }
  }

  // Second, add active speakers
  let activeSpeakers: string[] = [];
  let maximumNumberOfActiveSpeakers = 0;

  if (activeSpeakersWithCameraSource.length > 0) {

    if (layout === Layout.Gallery) {
      maximumNumberOfActiveSpeakers = 1;
    }
    if (layout === Layout.Featured) {
      maximumNumberOfActiveSpeakers =
        4 - (hasLocalVideo ? 1 : 0) - (contentShareId ? 1 : 0);
    }

    activeSpeakers = activeSpeakersWithCameraSource.slice(0, maximumNumberOfActiveSpeakers);

    videoSources.push(
      ...activeSpeakers.map(attendeeId => ({
        attendeeId,
        type: VideoSourceType.ACTIVE_SPEAKER,
      }))
    );

    commonSources = cameraSources.filter(
      id => !activeSpeakers.includes(id)
    );
  } else {
    commonSources = cameraSources;
  }


  // Last, add common video sources
  let gridSize = 0;

  if (layout === Layout.Gallery) {
    gridSize = Number.MAX_SAFE_INTEGER;
  }

  if (layout === Layout.Featured) {
    gridSize = 4;
  }

  const numberOfAvailableTiles = gridSize - (hasLocalVideo ? 1 : 0) - (contentShareId ? 1 : 0) - activeSpeakers.length;

  videoSources.push(
    ...commonSources
      .slice(0, numberOfAvailableTiles)
      .map(attendeeId => ({ attendeeId, type: VideoSourceType.OTHER }))
  );

  return videoSources;
};

// ---------------------------------------------------------------------------
// Bandwidth-aware preference building
// ---------------------------------------------------------------------------

/** Bandwidth pressure tiers. Drives per-source target display size and
 *  the suppression-intent set returned to the caller. Boundaries are
 *  first-principles starting values; revisit against production data. */
export type BandwidthTier = 'healthy' | 'mild' | 'moderate' | 'severe' | 'critical';

const TIER_ORDER: BandwidthTier[] = ['healthy', 'mild', 'moderate', 'severe', 'critical'];

/** Resolve the bandwidth tier from `available_incoming_bitrate` (Kbps).
 *  null is treated as healthy — cold-start safety; don't over-degrade
 *  before the metrics pipeline has produced a reading. */
export const resolveBandwidthTier = (availableKbps: number | null): BandwidthTier => {
  if (availableKbps === null || availableKbps >= 2000) return 'healthy';
  if (availableKbps >= 1000) return 'mild';
  if (availableKbps >= 500) return 'moderate';
  if (availableKbps >= 300) return 'severe';
  return 'critical';
};

/** Bump tier one row worse, clamped at 'critical'. Used by preemptive mode
 *  during the post-share-start window — we expect transient bandwidth
 *  pressure as the simulcast estimator settles, so we degrade preemptively
 *  to give the link headroom for the share. */
const stepDownTier = (tier: BandwidthTier): BandwidthTier => {
  const idx = TIER_ORDER.indexOf(tier);
  return TIER_ORDER[Math.min(idx + 1, TIER_ORDER.length - 1)];
};

export type UpdatePreferencesArgs = {
  gridState: GridState;
  videoSourceState: VideoSourceState;
  attendeeTileState: Record<string, TileState>;
  /** From `latestQualityMetrics?.available_incoming_bitrate / 1000`. null
   *  if the metrics tick hasn't fired yet. */
  availableIncomingBitrateKbps: number | null;
  /** Set during the 5-second post-share-start window to step the tier
   *  down one notch. Restored to false when the share-start preemptive
   *  timer fires its restore check. */
  preemptiveMode?: boolean;
};

export type PreferenceResult = {
  /** Camera attendees the policy decided to suppress at this tier. The
   *  caller writes this into `ChimeObserverState.suppressionIntent` so
   *  subsequent SDK pause callbacks can be tagged 'bandwidth_suppressed'
   *  vs ordinary 'frozen'. Suppressed attendees are STILL included in
   *  the preference set at TargetDisplaySize.Low priority 3 — the SDK's
   *  built-in pause-tile mechanism does the actual byte-level suppression
   *  and emits tileWillBePausedByDownlinkPolicy for them, which is the
   *  reliable recovery signal (omission would have no recovery callback). */
  suppressedAttendees: Set<string>;
  /** The tier that drove this update; surfaced for tests and dev logging. */
  tier: BandwidthTier;
};

/** Per-tier preference rules. `'suppress'` means the source goes into the
 *  preference set at Low/priority 3 AND its attendeeId is added to
 *  `suppressedAttendees`; the SDK then pauses it under bandwidth pressure. */
type TierRules = {
  contentShare: TargetDisplaySize;
  activeSpeaker: TargetDisplaySize;
  otherCamera: TargetDisplaySize | 'suppress';
};

const TIER_TABLE: Record<BandwidthTier, TierRules> = {
  healthy: {
    contentShare: TargetDisplaySize.High,
    activeSpeaker: TargetDisplaySize.High,
    otherCamera: TargetDisplaySize.Medium,
  },
  mild: {
    contentShare: TargetDisplaySize.High,
    activeSpeaker: TargetDisplaySize.Medium,
    otherCamera: TargetDisplaySize.Low,
  },
  moderate: {
    contentShare: TargetDisplaySize.Medium,
    activeSpeaker: TargetDisplaySize.Medium,
    otherCamera: TargetDisplaySize.Low,
  },
  severe: {
    contentShare: TargetDisplaySize.Low,
    activeSpeaker: TargetDisplaySize.Low,
    otherCamera: 'suppress',
  },
  critical: {
    contentShare: TargetDisplaySize.Low,
    activeSpeaker: 'suppress' as unknown as TargetDisplaySize,
    otherCamera: 'suppress',
  },
};

/** Build and apply video preferences with bandwidth-aware target sizes,
 *  audio implicitly protected (audio is independent of the video
 *  preference set in the Chime SDK), and an explicit suppression-intent
 *  return so the caller can drive the bandwidth_suppressed tile state.
 *
 *  Preference-set membership semantics:
 *  - All non-suppressed sources land in the set at their tier-derived
 *    TargetDisplaySize and priority 1 (content/active speaker) or 2 (other).
 *  - Suppressed sources STILL land in the set at TargetDisplaySize.Low and
 *    priority 3 (lowest). The SDK's chooseRemoteVideoSources iterates the
 *    set; under bandwidth pressure it pauses the lowest-priority tiles
 *    via tileWillBePausedByDownlinkPolicy. Omission was considered and
 *    rejected — the SDK has no recovery callback for omitted sources, so
 *    a previously-omitted attendee whose SFU-side availability is
 *    unchanged would never re-render. */
export const updateDownlinkPreferences = (
  args: UpdatePreferencesArgs,
  policy: VideoPriorityBasedPolicy | undefined
): PreferenceResult => {
  const baseTier = resolveBandwidthTier(args.availableIncomingBitrateKbps);
  const tier: BandwidthTier = args.preemptiveMode ? stepDownTier(baseTier) : baseTier;
  const rules = TIER_TABLE[tier];
  const suppressedAttendees = new Set<string>();

  if (!policy) {
    return { suppressedAttendees, tier };
  }

  const { layout } = args.gridState;
  const { hasLocalVideo } = args.videoSourceState;
  const videoPreferences = VideoPreferences.prepare();

  const videoSourcesToBeRendered = calculateVideoSourcesToBeRendered(
    args.gridState,
    args.videoSourceState,
    args.attendeeTileState
  );

  // For the `'healthy'` tier, fall back to the original tile-count rule for
  // active-speaker / other-camera sizes. This preserves the existing
  // good-network behavior — the healthy tier shouldn't down-rev based on
  // headroom we have.
  const numberOfTiles =
    videoSourcesToBeRendered.length + (hasLocalVideo ? 1 : 0);
  const HIGH_THRESHOLD = 4;
  const MEDIUM_THRESHOLD = 8;
  let healthyTargetSize: TargetDisplaySize;
  if (numberOfTiles <= HIGH_THRESHOLD) {
    healthyTargetSize = TargetDisplaySize.High;
  } else if (numberOfTiles <= MEDIUM_THRESHOLD) {
    healthyTargetSize = TargetDisplaySize.Medium;
  } else {
    healthyTargetSize = TargetDisplaySize.Low;
  }

  for (const videoSource of videoSourcesToBeRendered) {
    const { attendeeId, type } = videoSource;

    switch (type) {
      case VideoSourceType.CONTENT_SHARE: {
        // Content always rendered. Tier dictates resolution.
        const size = tier === 'healthy'
          ? (numberOfTiles > MEDIUM_THRESHOLD ? TargetDisplaySize.Medium : TargetDisplaySize.High)
          : rules.contentShare;
        videoPreferences.add(new VideoPreference(attendeeId, 1, size));
        break;
      }

      case VideoSourceType.ACTIVE_SPEAKER: {
        if (rules.activeSpeaker === ('suppress' as unknown as TargetDisplaySize)) {
          // 'critical' tier — active speaker suppressed. Stays in preference
          // set at Low/priority 3 so SDK pauses it; recovery via unpause
          // callback fires when bandwidth allows.
          suppressedAttendees.add(attendeeId);
          videoPreferences.add(new VideoPreference(attendeeId, 3, TargetDisplaySize.Low));
        } else if (tier === 'healthy') {
          // Preserve original behavior at healthy: Featured layout always
          // High; Gallery uses the tile-count rule.
          videoPreferences.add(
            new VideoPreference(
              attendeeId,
              1,
              layout === Layout.Featured ? TargetDisplaySize.High : healthyTargetSize
            )
          );
        } else {
          videoPreferences.add(new VideoPreference(attendeeId, 1, rules.activeSpeaker));
        }
        break;
      }

      default: { // OTHER
        if (rules.otherCamera === 'suppress') {
          suppressedAttendees.add(attendeeId);
          videoPreferences.add(new VideoPreference(attendeeId, 3, TargetDisplaySize.Low));
        } else if (tier === 'healthy') {
          videoPreferences.add(new VideoPreference(attendeeId, 2, healthyTargetSize));
        } else {
          videoPreferences.add(new VideoPreference(attendeeId, 2, rules.otherCamera));
        }
        break;
      }
    }
  }

  policy.chooseRemoteVideoSources(videoPreferences.build());
  return { suppressedAttendees, tier };
};
